/**
 * **打印作业**（份数 / 切刀 / 浓度…）—— 模板里"这一份活怎么打"的那部分。
 *
 * ## 为什么它在文档上、而不是一个新节点
 *
 * 架构里那条结论是"**文档模型 = 节点图**"：一份模板就是一份文档，
 * 而"这份文档怎么打"是**文档级**的属性 —— 和纸张、分辨率同一条道理。
 * 单开一个「作业节点」要用户先去连一条线才能改份数，而且会与文档节点的角色重叠
 * （文档已经是这份模板的根了）。
 *
 * ## 为什么这些字段进 IR，而 ZPL 不进
 *
 * 按 **D7**：**设备指令（ZPL / TSPL / ESC-POS）只在 C++ 里生成** ——
 * 它是纯输出侧逻辑，天然不需要两端一致（各机型语法还不一样）。
 * 但"打 3 份、每张切一刀、浓度 12"是**模板语义**，必须由这边表达、
 * 进 IR、由执行侧翻译成它那台机器的指令。
 *
 * ⚠️ 所以这里的字段刻意都选**机型无关**的说法（份数 / 切 / 浓度 0–30 / 速度档 /
 * 介质 / 走纸 mm），不写 `^MD`、`DENSITY` 这类具体指令名 —— 那是执行侧的事。
 */

/** 切刀：打完这一份之后要不要切 */
export type PrintCut = 'none' | 'after-each' | 'after-job'
/** 介质：热敏机上"怎么判断一张纸到哪儿了" */
export type PrintMedia = 'gap' | 'black-mark' | 'continuous'
/** 速度档（具体 mm/s 由执行侧按机型映射） */
export type PrintSpeed = 'slow' | 'normal' | 'fast'

export interface PrintJob {
  /** 份数（同一份内容打几遍） */
  copies: number
  cut: PrintCut
  /**
   * **浓度 / 黑度**（0–30，相对值）。
   *
   * ⚠️ 相对值而不是绝对值：ZPL 的 `^MD` 是 -30..30、TSPL 的 `DENSITY` 是 0..15，
   * 每台机器的"12"实际影响都不一样。执行侧按自己的量程映射。
   */
  darkness: number
  speed: PrintSpeed
  media: PrintMedia
  /** 打完额外走纸（mm）—— 撕纸位 / 剥离位对不齐时用它微调 */
  feedMm: number
}

/**
 * 默认作业：**一份、不切、浓度 12、标准速度、间隙纸、不走纸**。
 *
 * ⚠️ 只有这一处默认值（和 `DEFAULT_PAPER` 同一条规矩）：参数表那边**引用**它，
 * 不再各写一份数字 —— 否则"新建文档"和"没有作业时的兜底"会不一致。
 */
export const DEFAULT_JOB: PrintJob = {
  copies: 1,
  cut: 'none',
  darkness: 12,
  speed: 'normal',
  media: 'gap',
  feedMm: 0,
}

/** 夹到合法范围（值可能来自别处：手改的文档、导入的模板） */
export function normalizeJob(job: Partial<PrintJob> | undefined): PrintJob {
  const source = { ...DEFAULT_JOB, ...(job ?? {}) }
  const int = (value: unknown, fallback: number, min: number, max: number): number => {
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return fallback
    return Math.min(max, Math.max(min, Math.round(numeric)))
  }
  return {
    copies: int(source.copies, DEFAULT_JOB.copies, 1, 999),
    cut: source.cut === 'after-each' || source.cut === 'after-job' ? source.cut : 'none',
    darkness: int(source.darkness, DEFAULT_JOB.darkness, 0, 30),
    speed: source.speed === 'slow' || source.speed === 'fast' ? source.speed : 'normal',
    media:
      source.media === 'black-mark' || source.media === 'continuous' ? source.media : 'gap',
    feedMm: Math.min(50, Math.max(0, Number(source.feedMm) || 0)),
  }
}

/** 给人看的一句话（面板 / 标签上显示"这份活怎么打"） */
export function describeJob(job: PrintJob): string {
  const cut =
    job.cut === 'after-each' ? '每张切' : job.cut === 'after-job' ? '打完切' : '不切'
  const media =
    job.media === 'gap' ? '间隙纸' : job.media === 'black-mark' ? '黑标纸' : '连续纸'
  const speed = job.speed === 'slow' ? '慢' : job.speed === 'fast' ? '快' : '标准'
  const feed = job.feedMm > 0 ? ` · 走纸 ${job.feedMm}mm` : ''
  return `${job.copies} 份 · ${cut} · 浓度 ${job.darkness} · ${speed} · ${media}${feed}`
}
